import { Box, Popover, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BriaButton from "../../../../../../../components/common/BriaButton/BriaButton";
import { HorizontalEnum, VerticalEnum } from "../../../../../../../models/common";
import { Property } from "../../../../../../CustomUtils/UseDesignEditor";
import BottomCenter from "../../../../../../assets/svgs/BottomCenter.svg";
import CenterCenter from "../../../../../../assets/svgs/CenterCenter.svg";
import CenterLeft from "../../../../../../assets/svgs/CenterLeft.svg";
import CenterRight from "../../../../../../assets/svgs/CenterRight.svg";
import TopCenter from "../../../../../../assets/svgs/TopCenter.svg";
import TopLeft from "../../../../../../assets/svgs/TopLeft.svg";
import TopRight from "../../../../../../assets/svgs/TopRight.svg";
import BottomLeft from "../../../../../../assets/svgs/box-align-bottom-left.svg";
import BottomRight from "../../../../../../assets/svgs/box-align-bottom-right.svg";
import { useActiveObject } from "../../../../../../hooks/useActiveObject";
import UseBrandConfigs from "../../../../../../hooks/useBrandConfigs";
import { useEditor } from "../../../../../../hooks/useEditor";
import styles from "./ImageAlignment.module.scss";

const ImageAlignment = () => {
	const { t } = useTranslation("translation", { keyPrefix: "editor.controllers.alignmentControllers" });
	const { modifyActiveObjectProperty } = UseBrandConfigs();
	const activeObject = useActiveObject() as Required<fabric.Object>;
	const [alignOption, setAlignOption] = useState<any>({
		horizontalAlignment: activeObject?.horizontalAlignment ?? HorizontalEnum.CENTER,
		verticalAlignment: activeObject?.verticalAlignment ?? VerticalEnum.CENTER,
	});

	const editor = useEditor();
	const [anchorEl, seAnchorEl] = useState<HTMLButtonElement>();

	useEffect(() => {
		let watcher = async () => {
			if (activeObject) {
				setAlignOption({
					horizontalAlignment: activeObject?.horizontalAlignment ?? HorizontalEnum.CENTER,
					verticalAlignment: activeObject?.verticalAlignment ?? VerticalEnum.CENTER,
				});
			}
		};
		if (editor) {
			editor.on("history:changed", watcher);
		}
		return () => {
			if (editor) {
				editor.off("history:changed", watcher);
			}
		};
	}, [editor, activeObject]);

	const handleOpenAlignmentDialog = (event: React.MouseEvent<HTMLButtonElement>) => {
		seAnchorEl(event.currentTarget);
	};
	const handleCloseAlignmentDialog = () => {
		seAnchorEl(undefined);
	};

	const modifyActiveObjectlogoHorizontalAlignment = (
		horizontalAlignment: HorizontalEnum,
		verticalAlignment: VerticalEnum,
	) => {
		modifyActiveObjectProperty(activeObject, Property.HORIZONTAL_ALIGNMENT, horizontalAlignment);
		modifyActiveObjectProperty(activeObject, Property.VERTICAL_ALIGNMENT, verticalAlignment);
	};

	const alignmentsOptions = [
		{
			icon: TopLeft,
			horizontal: HorizontalEnum.LEFT,
			vertical: VerticalEnum.TOP,
			onClick: () => {
				modifyActiveObjectlogoHorizontalAlignment(HorizontalEnum.LEFT, VerticalEnum.TOP);
			},
		},
		{
			icon: TopCenter,
			horizontal: HorizontalEnum.CENTER,
			vertical: VerticalEnum.TOP,
			onClick: () => {
				modifyActiveObjectlogoHorizontalAlignment(HorizontalEnum.CENTER, VerticalEnum.TOP);
			},
		},
		{
			icon: TopRight,
			horizontal: HorizontalEnum.RIGHT,
			vertical: VerticalEnum.TOP,
			onClick: () => {
				modifyActiveObjectlogoHorizontalAlignment(HorizontalEnum.RIGHT, VerticalEnum.TOP);
			},
		},
		{
			icon: CenterLeft,
			horizontal: HorizontalEnum.LEFT,
			vertical: VerticalEnum.CENTER,
			onClick: () => {
				modifyActiveObjectlogoHorizontalAlignment(HorizontalEnum.LEFT, VerticalEnum.CENTER);
			},
		},
		{
			icon: CenterCenter,
			horizontal: HorizontalEnum.CENTER,
			vertical: VerticalEnum.CENTER,
			onClick: () => {
				modifyActiveObjectlogoHorizontalAlignment(HorizontalEnum.CENTER, VerticalEnum.CENTER);
			},
		},
		{
			icon: CenterRight,
			horizontal: HorizontalEnum.RIGHT,
			vertical: VerticalEnum.CENTER,
			onClick: () => {
				modifyActiveObjectlogoHorizontalAlignment(HorizontalEnum.RIGHT, VerticalEnum.CENTER);
			},
		},
		{
			icon: BottomLeft,
			horizontal: HorizontalEnum.LEFT,
			vertical: VerticalEnum.BOTTOM,
			onClick: () => {
				modifyActiveObjectlogoHorizontalAlignment(HorizontalEnum.LEFT, VerticalEnum.BOTTOM);
			},
		},
		{
			icon: BottomCenter,
			horizontal: HorizontalEnum.CENTER,
			vertical: VerticalEnum.BOTTOM,
			onClick: () => {
				modifyActiveObjectlogoHorizontalAlignment(HorizontalEnum.CENTER, VerticalEnum.BOTTOM);
			},
		},
		{
			icon: BottomRight,
			horizontal: HorizontalEnum.RIGHT,
			vertical: VerticalEnum.BOTTOM,
			onClick: () => {
				modifyActiveObjectlogoHorizontalAlignment(HorizontalEnum.RIGHT, VerticalEnum.BOTTOM);
			},
		},
	];

	return (
		<Box className={styles.alignmentWrapper}>
			<BriaButton
				buttonType="textMedium"
				onClick={handleOpenAlignmentDialog}
				className={clsx({
					[styles.button]: true,
					[styles.propertiesButtonSelected]: !!anchorEl,
				})}
			>
				<img src={CenterCenter} />
				<Typography className={styles.label}>{t("alignment")}</Typography>
			</BriaButton>
			<Popover
				disableRestoreFocus
				open={!!anchorEl}
				onClose={handleCloseAlignmentDialog}
				anchorEl={anchorEl}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
				slotProps={{
					paper: {
						className: styles.popover,
					},
				}}
			>
				<Box className={styles.alignmentContainer}>
					{alignmentsOptions.map((option, index) => (
						<img
							key={index}
							src={option.icon}
							alt={`alignment-${index}`}
							className={clsx(styles.alignmentOption, {
								[styles.selected]:
									alignOption.horizontalAlignment === option.horizontal &&
									alignOption.verticalAlignment === option.vertical,
							})}
							onClick={option.onClick}
						/>
					))}
				</Box>
			</Popover>
		</Box>
	);
};

const ObservedComponent = observer(ImageAlignment);
export default ObservedComponent;
